import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import { showToast } from '../components/Toast';
import StatusBadge from '../components/StatusBadge';
import BatteryMeter from '../components/BatteryMeter';
import CompetitionBottomNav from '../components/CompetitionBottomNav';
import { getDisplayName, getTeamMeta, formatScoreAbbreviated, TeamIcon, compareByStasisThenScore, countInContention } from '../utils/transformersCompetition';
import RoundCountdown from '../components/RoundCountdown';
import transformersHero from '../assets/transformers-hero.webp';
import './transformersCompetitionShared.css';

// A player's raw-point contribution to their team this week: how far above target their best
// score is, floored at 0 (missing target contributes nothing, same as the real percentage-based
// Energon Surplus rule - this is just a points-flavored view of the same "did they beat target"
// idea for display, not a replacement for the actual ranking math).
function memberPointsSurplus(member) {
  return member.score > member.targetWithRust ? member.score - member.targetWithRust : 0;
}

function TeamRankRow({ team, rank, pointsTotal, maxPointsTotal, defaultExpanded }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const inContentionCount = countInContention(team.members);
  const sortedMembers = [...team.members].sort(compareByStasisThenScore);
  // Where the in-contention players end and the Stasis Locked ones begin. -1 when the team is
  // entirely one or the other, in which case no divider is drawn.
  const firstLockedIndex = sortedMembers.findIndex(m => m.isStasisLocked);
  const lockedDividerIndex = firstLockedIndex > 0 ? firstLockedIndex : -1;

  return (
    <div className={`tc-section ${team.isWeeklyWinner ? 'tc-section-winner' : ''}`}>
      <button type="button" className="tc-team-standing-row tc-team-standing-row-flat" onClick={() => setExpanded(!expanded)}>
        <span className="tc-week-toggle-chevron">{expanded ? '▾' : '▸'}</span>
        <span className="tc-team-standing-rank">#{rank}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span className={`tc-team-badge team-${team.id}`}><TeamIcon teamId={team.id} size={16} /> {team.name}</span>
          {team.isWeeklyWinner && (
            <StatusBadge variant="success" uppercase size="sm" className="tc-inline-badge">Leading</StatusBadge>
          )}
          <div className="tc-team-standing-bar-track" style={{ marginTop: 6 }}>
            <div
              className="tc-team-standing-bar-fill"
              style={{ width: `${Math.max(2, (pointsTotal / maxPointsTotal) * 100)}%`, background: getTeamMeta(team.id).color }}
            />
          </div>
          <span className="tc-form-hint" style={{ marginTop: 4, display: 'block' }}>
            {team.members.length} members
            {' - '}<span className="tc-roster-in-contention-count">{inContentionCount} in contention</span>
          </span>
        </div>
        <span className="tc-team-standing-value">+{formatScoreAbbreviated(pointsTotal)}</span>
      </button>

      {expanded && (
        <div className="tc-roster-list" style={{ marginTop: 14 }}>
          {sortedMembers.map((member, index) => {
            const points = memberPointsSurplus(member);
            const key = member.userId || member.sternUsername;
            return (
              <React.Fragment key={key}>
                {index === lockedDividerIndex && (
                  <div className="tc-roster-divider">Stasis Locked</div>
                )}
                <div className={`tc-roster-row ${member.isStasisLocked ? 'is-locked' : ''}`}>
                  <div className="tc-roster-player">
                    <span className="tc-roster-player-name">{getDisplayName(member)}</span>
                    <span className="tc-roster-player-sub">
                      Score: {member.score > 0 ? formatScoreAbbreviated(member.score) : 'No play yet'}
                      {member.targetWithRust ? ` / Target: ${formatScoreAbbreviated(member.targetWithRust)}` : ''}
                    </span>
                  </div>
                  <div className="tc-roster-metrics">
                    {member.rustPoints > 0 && <span className="tc-rust-badge">Rust {member.rustPoints}</span>}
                    <span className="tc-surplus-highlight" style={{ color: points > 0 ? 'var(--tc-batt-good)' : 'var(--tc-text-dim)' }}>
                      {points > 0 ? `+${formatScoreAbbreviated(points)}` : ' - '}
                    </span>
                    <BatteryMeter battery={member.energonBattery} isStasisLocked={member.isStasisLocked} />
                  </div>
                </div>
              </React.Fragment>
            );
          })}
          {team.members.length === 0 && <p>No players on this team yet.</p>}
        </div>
      )}
    </div>
  );
}

// Member-facing "how's the season going" view - reachable by any logged-in member via the
// bottom nav. Distinct from the public TV dashboard (which is a fixed kiosk layout meant only
// for a physical screen in the club): this is a normal in-app page, and unlike the earlier
// "danger zone" framing, it lists every player's full weekly breakdown, not just the lowest
// Battery ones.
function CompetitionBroadcast() {
  const [competition, setCompetition] = useState(null);
  const [standings, setStandings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const compRes = await apiService.getCurrentCompetition();
      setCompetition(compRes.data);

      if (compRes.data?.current_round_id) {
        const standingsRes = await apiService.getCompetitionRoundStandings(compRes.data.id, compRes.data.current_round_id);
        setStandings(standingsRes.data);
      } else {
        setStandings(null);
      }
    } catch (err) {
      setError(err.response?.status === 404 ? '' : (err.response?.data?.error || 'Unable to load competition'));
      setCompetition(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <div className="loading">Loading...</div>;

  const tvUrl = competition ? `${window.location.origin}/competitions/${competition.id}/tv` : '';

  const handleOpenTv = () => {
    window.open(tvUrl, '_blank', 'noopener');
  };

  const handleCopyTvLink = async () => {
    try {
      await navigator.clipboard.writeText(tvUrl);
      showToast('TV dashboard link copied to clipboard.', 'success');
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.prompt('Copy this link:', tvUrl);
    }
  };

  const rankedTeams = standings?.teams
    ? [...standings.teams].sort((a, b) => (b.energonTotal || 0) - (a.energonTotal || 0))
    : [];
  const teamPointsTotals = rankedTeams.map(team => team.members.reduce((sum, m) => sum + memberPointsSurplus(m), 0));
  const maxPointsTotal = Math.max(1, ...teamPointsTotals);

  return (
    <div className="tc-page">
      <div className="tc-hero">
        <img src={transformersHero} alt="Transformers: More Than Meets The Eye pinball machine artwork" className="tc-hero-banner" />
        <h1>Transformers Pinball Championship</h1>
        <p>
          {competition
            ? (standings?.round ? `Week ${standings.round.round_number} - ${standings.round.status === 'active' ? 'In Progress' : 'Just Closed'}` : competition.status.replace('_', ' '))
            : 'No season in progress right now'}
        </p>
        {standings?.round?.status === 'active' && (
          <div style={{ marginTop: 8, maxWidth: 260 }}>
            <RoundCountdown />
          </div>
        )}
      </div>

      {error && <div className="tc-danger-box">{error}</div>}

      {competition && competition.status === 'completed' && (competition.winner_user_id || competition.winner_stern_username) && (
        <div className="tc-success-box">
          Grand Champion: {competition.winner_user_id
            ? `${competition.winner_first_name} ${competition.winner_last_name}`
            : `${competition.winner_stern_username} (no account linked yet)`}
        </div>
      )}

      {!competition && !error && (
        <div className="tc-empty-state">
          <h2>No season in progress</h2>
          <p>Check back once a Transformers Championship season is underway.</p>
        </div>
      )}

      {rankedTeams.map((team, index) => (
        <TeamRankRow
          key={team.id}
          team={team}
          rank={index + 1}
          pointsTotal={teamPointsTotals[index]}
          maxPointsTotal={maxPointsTotal}
          defaultExpanded={false}
        />
      ))}

      {competition && (
        <div className="tc-section">
          <div className="tc-section-header-row">
            <h2>TV Dashboard</h2>
          </div>
          <p className="tc-form-hint">
            Open the live standings on your phone, or copy the link to share.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="tc-btn tc-btn-primary" onClick={handleOpenTv}>
              Open TV View &#8599;
            </button>
            <button type="button" className="tc-btn tc-btn-secondary" onClick={handleCopyTvLink}>
              Copy Link
            </button>
          </div>
        </div>
      )}

      <CompetitionBottomNav />
    </div>
  );
}

export default CompetitionBroadcast;
